import React, { useState, useEffect, useRef } from 'react';
import { C, WEEKS, WEEK_TOPICS } from '../data/constants';
import { NudgeLogo, ProgressBar, PrimaryBtn, SecondaryBtn } from './shared/UI';
import WeekInfographic from './Infographics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONV_KEY     = 'nudge-conversations-v1';

// ─── helpers ──────────────────────────────────────────────────────────────────
function loadConversations() {
  try { return JSON.parse(localStorage.getItem(CONV_KEY) || '{}'); } catch { return {}; }
}
function saveConversations(obj) {
  try { localStorage.setItem(CONV_KEY, JSON.stringify(obj)); } catch {}
}

// ─── Call Claude via backend ──────────────────────────────────────────────────
async function callClaude({ messages, profile, week, day, topic, sessionId }) {
  const resp = await fetch(`${BACKEND_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages: messages.map(m => ({ role: m.role, content: m.content })),
      profile:    profile || {},
      week,
      day,
      topic,
      session_id: sessionId,
    }),
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  const data = await resp.json();
  return data.response;
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function Coaching({ user, onComplete, onProgress, onLogout }) {
  const [view, setView]         = useState('dashboard');
  const [week, setWeek]         = useState(user?.coachingWeek ?? 0);
  const [day,  setDay]          = useState(user?.coachingDay  ?? 0);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showInfographic, setShowInfographic] = useState(false);

  const convsRef    = useRef(loadConversations());
  const messagesEnd = useRef(null);

  const sessionKey  = `${week}-${day}`;
  const isLastSession = week === 3 && day === 5;
  const totalDone   = week * 6 + day;

  // ── scroll to bottom on new messages ──────────────────────────────────────
  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── open / resume a chat session ──────────────────────────────────────────
  useEffect(() => {
    if (view !== 'chat') return;

    const saved = convsRef.current[sessionKey];
    if (saved?.length) {
      // Resuming existing session — skip infographic, go straight to chat
      setMessages(saved);
      setShowInfographic(false);
      return;
    }

    // Fresh session — show infographic first (only on day 0 of each week for week overview)
    if (day === 0) {
      setShowInfographic(true);
      return;
    }

    // For days 1-5, start chat directly without infographic
    startChatSession();
  }, [view, week, day]); // eslint-disable-line

  // ── Start the chat session (called after infographic or directly) ────────
  const startChatSession = () => {
    setShowInfographic(false);
    
    const sid = `${user?.studyId || 'anon'}-w${week}-d${day}-${Date.now()}`;
    setSessionId(sid);
    setAiLoading(true);

    const openingPrompt = {
      role: 'user',
      content: 'Please begin today\'s session.',
    };

    callClaude({
      messages:  [openingPrompt],
      profile:   user?.profile || {},
      week,
      day,
      topic:     WEEK_TOPICS[week][day],
      sessionId: sid,
    })
      .then(text => {
        const intro = { role: 'assistant', content: text, ts: Date.now() };
        const newMsgs = [intro];
        setMessages(newMsgs);
        convsRef.current = { ...convsRef.current, [sessionKey]: newMsgs };
        saveConversations(convsRef.current);
      })
      .catch(() => {
        const fallback = {
          role: 'assistant',
          content: `Welcome to Week ${week + 1}, Session ${day + 1}: **${WEEK_TOPICS[week][day]}**.\n\nLet's start by exploring where you currently stand on this topic. Before I share any concepts, walk me through what you already know or believe about ${WEEK_TOPICS[week][day].toLowerCase()}. What comes to mind first?`,
          ts: Date.now(),
        };
        setMessages([fallback]);
        convsRef.current = { ...convsRef.current, [sessionKey]: [fallback] };
        saveConversations(convsRef.current);
      })
      .finally(() => setAiLoading(false));
  };

  // ── send a user message ────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!input.trim() || aiLoading) return;

    const sid = sessionId || `${user?.studyId || 'anon'}-w${week}-d${day}-${Date.now()}`;
    if (!sessionId) setSessionId(sid);

    const userMsg  = { role: 'user', content: input.trim(), ts: Date.now() };
    const newMsgs  = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    setAiLoading(true);

    try {
      const text = await callClaude({
        messages:  newMsgs,
        profile:   user?.profile || {},
        week,
        day,
        topic:     WEEK_TOPICS[week][day],
        sessionId: sid,
      });
      const aiMsg  = { role: 'assistant', content: text, ts: Date.now() };
      const final  = [...newMsgs, aiMsg];
      setMessages(final);
      convsRef.current = { ...convsRef.current, [sessionKey]: final };
      saveConversations(convsRef.current);
    } catch (e) {
      const errMsg = {
        role: 'assistant',
        content: 'I\'m having trouble connecting right now. Please try again in a moment.',
        ts: Date.now(),
        isError: true,
      };
      const final = [...newMsgs, errMsg];
      setMessages(final);
    } finally {
      setAiLoading(false);
    }
  };

  // ── finish current session, move to next ──────────────────────────────────
  const finishSession = () => {
    let nextWeek = week, nextDay = day + 1;
    if (nextDay >= 6) { nextWeek++; nextDay = 0; }
    if (nextWeek >= 4) { onComplete(); return; }
    onProgress(nextWeek, nextDay);
    setWeek(nextWeek);
    setDay(nextDay);
    setView('dashboard');
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DASHBOARD VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'dashboard') {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
        {/* Navbar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: 'rgba(250,251,252,0.96)', backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${C.border}`, padding: '0 24px',
        }}>
          <div style={{ maxWidth: 900, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <NudgeLogo size="sm" />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {user?.studyId && <span style={{ fontSize: 12, color: C.muted }}>{user.studyId}</span>}
              <button onClick={onLogout} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 13, color: C.body, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                Log out
              </button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px 60px' }}>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, color: C.navy, fontWeight: 400, marginBottom: 6 }}>Your Progress</h1>
          <p style={{ color: C.muted, fontSize: 15, marginBottom: 28 }}>4-Week Financial Coaching Program</p>

          <div style={{ marginBottom: 36 }}>
            <ProgressBar current={totalDone} total={24} label="Overall Progress" />
          </div>

          {/* Week cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20, marginBottom: 36 }}>
            {WEEKS.map((weekName, wi) => {
              const isComplete = wi < week;
              const isCurrent  = wi === week;
              const isUpcoming = wi > week;
              return (
                <div key={wi} style={{
                  background: C.card,
                  border: isCurrent ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                  borderRadius: 12, padding: 22, opacity: isUpcoming ? 0.55 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: C.navy, fontWeight: 400, margin: 0 }}>
                      Week {wi + 1}: {weekName}
                    </h3>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      background: isComplete ? '#dcfce7' : isCurrent ? C.tealLight : C.border,
                      color: isComplete ? '#059669' : isCurrent ? C.teal : C.muted,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      {isComplete ? 'COMPLETED' : isCurrent ? 'CURRENT' : 'UPCOMING'}
                    </span>
                  </div>
                  {(isComplete || isCurrent) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {WEEK_TOPICS[wi].map((topic, di) => {
                        const done = isComplete || (isCurrent && di < day);
                        return (
                          <span key={di} style={{
                            fontSize: 11, padding: '3px 8px', borderRadius: 4,
                            background: done ? '#dcfce7' : '#f1f5f9',
                            color: done ? '#059669' : C.muted,
                          }}>{topic}</span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <PrimaryBtn onClick={() => setView('chat')} style={{ fontSize: 15, padding: '13px 32px' }}>
            Continue Coaching &rarr;
          </PrimaryBtn>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CHAT VIEW - Show Infographic First (if new week start), then Chat
  // ══════════════════════════════════════════════════════════════════════════
  
  // Show infographic for the first session of each week
  if (view === 'chat' && showInfographic) {
    return <WeekInfographic week={week} onBegin={startChatSession} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>

      {/* Chat header */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: '0 24px', background: 'rgba(250,251,252,0.96)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 14, color: C.navy, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Week {week + 1}: {WEEKS[week]} — Session {day + 1}
            </p>
            <div style={{ marginTop: 5 }}>
              <ProgressBar current={totalDone + 1} total={24} />
            </div>
          </div>
          <SecondaryBtn onClick={() => setView('dashboard')} style={{ padding: '6px 14px', fontSize: 13, flexShrink: 0 }}>
            Dashboard
          </SecondaryBtn>
        </div>
      </div>

      {/* Topic pill */}
      <div style={{ borderBottom: `1px solid ${C.border}`, background: '#fff', padding: '8px 24px', flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.teal, background: C.tealLight, borderRadius: 20, padding: '3px 12px' }}>
            Today: {WEEK_TOPICS[week][day]}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {aiLoading && messages.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ maxWidth: '82%' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Nudge</div>
                <div style={{ padding: '14px 18px', borderRadius: '14px 14px 14px 4px', background: C.card, border: `1px solid ${C.border}`, color: C.muted, fontSize: 14 }}>
                  <ThinkingDots />
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '82%' }}>
                {msg.role === 'assistant' && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: msg.isError ? C.coral : C.teal, marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Nudge
                  </div>
                )}
                <div style={{
                  padding: '13px 17px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.role === 'user' ? C.teal : C.card,
                  border: msg.role === 'user' ? 'none' : `1px solid ${msg.isError ? '#fecaca' : C.border}`,
                  color: msg.role === 'user' ? '#fff' : (msg.isError ? '#dc2626' : C.text),
                  fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap',
                }}>
                  <FormattedMessage text={msg.content} isUser={msg.role === 'user'} />
                </div>
              </div>
            </div>
          ))}

          {aiLoading && messages.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ maxWidth: '82%' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Nudge</div>
                <div style={{ padding: '13px 17px', borderRadius: '14px 14px 14px 4px', background: C.card, border: `1px solid ${C.border}`, color: C.muted, fontSize: 14 }}>
                  <ThinkingDots />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEnd} />
        </div>
      </div>

      {/* Input bar */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 24px', background: '#fff', flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Share your thoughts… (Enter to send, Shift+Enter for new line)"
            rows={2}
            style={{
              flex: 1, padding: '11px 14px',
              border: `1px solid ${C.border}`, borderRadius: 8,
              fontSize: 14, color: C.text, outline: 'none', resize: 'none',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <PrimaryBtn
              onClick={sendMessage}
              disabled={!input.trim() || aiLoading}
              style={{ padding: '10px 20px' }}
            >
              Send
            </PrimaryBtn>
            <SecondaryBtn
              onClick={finishSession}
              style={{ padding: '8px 12px', fontSize: 12 }}
            >
              {isLastSession ? 'Finish Program' : 'Next Session →'}
            </SecondaryBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Animated thinking dots ────────────────────────────────────────────────────
function ThinkingDots() {
  const [dot, setDot] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setDot(d => (d + 1) % 4), 420);
    return () => clearInterval(t);
  }, []);
  return <span>Nudge is thinking{'.'.repeat(dot)}</span>;
}

// ─── Render **bold** markdown in messages ─────────────────────────────────────
function FormattedMessage({ text, isUser }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
